/**
 * EditEntityModal — Edit entity details for web app.
 * Mirrors Tauri EditEntityModal for feature parity.
 */
import { useState, useEffect } from 'react';
import { useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { useAppStore } from '@/store/appStore';
import { X, Save, Building2, Music, Users, User } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Id } from '../../convex/_generated/dataModel';

interface EditEntityModalProps {
  entity: {
    _id: Id<'entities'>;
    name: string;
    displayName?: string;
    description?: string;
    entityType: string;
    bio?: string;
    contactInfo?: {
      email?: string;
      phone?: string;
      website?: string;
    };
    socialLinks?: {
      instagram?: string;
      twitter?: string;
      facebook?: string;
      soundcloud?: string;
    };
    brandColors?: string[];
  } | null;
  isOpen: boolean;
  onClose: () => void;
  onSaved?: () => void;
}

const ENTITY_TYPES = [
  { id: 'artist', label: 'Artist', icon: Music },
  { id: 'venue', label: 'Venue', icon: Building2 },
  { id: 'promoter', label: 'Promoter', icon: Users },
  { id: 'sponsor', label: 'Sponsor', icon: User },
];

export function EditEntityModal({ entity, isOpen, onClose, onSaved }: EditEntityModalProps) {
  const accountId = useAppStore((s) => s.currentAccountId) ?? undefined;
  const updateEntity = useMutation(api.entities.update);
  
  const [formData, setFormData] = useState({
    name: '',
    displayName: '',
    description: '',
    entityType: 'artist',
    bio: '',
    email: '',
    phone: '',
    website: '',
    instagram: '',
    twitter: '',
    facebook: '',
    soundcloud: '',
    brandColors: [] as string[],
  });
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'basic' | 'contact' | 'brand'>('basic');

  useEffect(() => {
    if (entity) {
      setFormData({
        name: entity.name || '',
        displayName: entity.displayName || '',
        description: entity.description || '',
        entityType: entity.entityType || 'artist',
        bio: entity.bio || '',
        email: entity.contactInfo?.email || '',
        phone: entity.contactInfo?.phone || '',
        website: entity.contactInfo?.website || '',
        instagram: entity.socialLinks?.instagram || '',
        twitter: entity.socialLinks?.twitter || '',
        facebook: entity.socialLinks?.facebook || '',
        soundcloud: entity.socialLinks?.soundcloud || '',
        brandColors: entity.brandColors || [],
      });
    }
  }, [entity]);

  const handleSave = async () => {
    if (!entity) return;
    
    setIsSaving(true);
    try {
      await updateEntity({
        accountId,
        id: entity._id,
        name: formData.name.trim(),
        displayName: formData.displayName.trim() || undefined,
        description: formData.description.trim() || undefined,
        bio: formData.bio.trim() || undefined,
        contactInfo: {
          email: formData.email.trim() || undefined,
          phone: formData.phone.trim() || undefined,
          website: formData.website.trim() || undefined,
        },
        socialLinks: {
          instagram: formData.instagram.trim() || undefined,
          twitter: formData.twitter.trim() || undefined,
          facebook: formData.facebook.trim() || undefined,
          soundcloud: formData.soundcloud.trim() || undefined,
        },
        brandColors: formData.brandColors.length > 0 ? formData.brandColors : undefined,
      });
      onSaved?.();
      onClose();
    } catch (err) {
      console.error('Save failed:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddColor = () => {
    setFormData((prev) => ({
      ...prev,
      brandColors: [...prev.brandColors, '#25a48f'],
    }));
  };

  const handleColorChange = (index: number, color: string) => {
    setFormData((prev) => ({
      ...prev,
      brandColors: prev.brandColors.map((c, i) => (i === index ? color : c)),
    }));
  };

  const handleRemoveColor = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      brandColors: prev.brandColors.filter((_, i) => i !== index),
    }));
  };

  if (!isOpen || !entity) return null;

  const typeMeta = ENTITY_TYPES.find((t) => t.id === formData.entityType);
  const TypeIcon = typeMeta?.icon || User;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="w-full max-w-lg rounded-xl border border-border bg-card shadow-lg flex flex-col max-h-[85vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <TypeIcon className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h2 className="font-semibold">Edit {typeMeta?.label || 'Entity'}</h2>
              <p className="text-xs text-muted-foreground">{entity.name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border">
          {(['basic', 'contact', 'brand'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={cn(
                "flex-1 px-4 py-2 text-sm font-medium capitalize transition-colors",
                activeTab === tab
                  ? "text-primary border-b-2 border-primary"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {activeTab === 'basic' && (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium block mb-1">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData((d) => ({ ...d, name: e.target.value }))}
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                  placeholder="Entity name"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium block mb-1">Display Name</label>
                <input
                  type="text"
                  value={formData.displayName}
                  onChange={(e) => setFormData((d) => ({ ...d, displayName: e.target.value }))}
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                  placeholder="Public display name"
                />
              </div>

              <div>
                <label className="text-sm font-medium block mb-1">Type</label>
                <select
                  value={formData.entityType}
                  onChange={(e) => setFormData((d) => ({ ...d, entityType: e.target.value }))}
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                >
                  {ENTITY_TYPES.map((t) => (
                    <option key={t.id} value={t.id}>{t.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-sm font-medium block mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData((d) => ({ ...d, description: e.target.value }))}
                  rows={3}
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm resize-none"
                  placeholder="Brief description"
                />
              </div>

              <div>
                <label className="text-sm font-medium block mb-1">Bio</label>
                <textarea
                  value={formData.bio}
                  onChange={(e) => setFormData((d) => ({ ...d, bio: e.target.value }))}
                  rows={4}
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm resize-none"
                  placeholder="Full biography"
                />
              </div>
            </div>
          )}

          {activeTab === 'contact' && (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium block mb-1">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData((d) => ({ ...d, email: e.target.value }))}
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                  placeholder="contact@example.com"
                />
              </div>

              <div>
                <label className="text-sm font-medium block mb-1">Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData((d) => ({ ...d, phone: e.target.value }))}
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                  placeholder="+1 234 567 890"
                />
              </div>

              <div>
                <label className="text-sm font-medium block mb-1">Website</label>
                <input
                  type="url"
                  value={formData.website}
                  onChange={(e) => setFormData((d) => ({ ...d, website: e.target.value }))}
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                  placeholder="https://example.com"
                />
              </div>

              <div className="border-t border-border pt-4">
                <h4 className="text-sm font-medium mb-3">Social Links</h4>
                
                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-muted-foreground block mb-1">Instagram</label>
                    <input
                      type="text"
                      value={formData.instagram}
                      onChange={(e) => setFormData((d) => ({ ...d, instagram: e.target.value }))}
                      className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                      placeholder="@username"
                    />
                  </div>

                  <div>
                    <label className="text-xs text-muted-foreground block mb-1">Twitter / X</label>
                    <input
                      type="text"
                      value={formData.twitter}
                      onChange={(e) => setFormData((d) => ({ ...d, twitter: e.target.value }))}
                      className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                      placeholder="@username"
                    />
                  </div>

                  <div>
                    <label className="text-xs text-muted-foreground block mb-1">Facebook</label>
                    <input
                      type="text"
                      value={formData.facebook}
                      onChange={(e) => setFormData((d) => ({ ...d, facebook: e.target.value }))}
                      className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                      placeholder="facebook.com/page"
                    />
                  </div>

                  <div>
                    <label className="text-xs text-muted-foreground block mb-1">SoundCloud</label>
                    <input
                      type="text"
                      value={formData.soundcloud}
                      onChange={(e) => setFormData((d) => ({ ...d, soundcloud: e.target.value }))}
                      className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                      placeholder="soundcloud.com/artist"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'brand' && (
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium">Brand Colors</label>
                  <button
                    onClick={handleAddColor}
                    className="text-xs text-primary hover:text-primary/80"
                  >
                    + Add color
                  </button>
                </div>
                <div className="space-y-2">
                  {formData.brandColors.map((color, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <input
                        type="color"
                        value={color}
                        onChange={(e) => handleColorChange(index, e.target.value)}
                        className="h-9 w-9 rounded border border-input cursor-pointer"
                      />
                      <input
                        type="text"
                        value={color}
                        onChange={(e) => handleColorChange(index, e.target.value)}
                        className="flex-1 rounded-lg border border-input bg-background px-3 py-2 text-sm"
                        placeholder="#25a48f"
                      />
                      <button
                        onClick={() => handleRemoveColor(index)}
                        className="p-2 text-muted-foreground hover:text-destructive"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  {formData.brandColors.length === 0 && (
                    <p className="text-sm text-muted-foreground">No brand colors added</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-border">
          <button
            onClick={onClose}
            className="rounded-lg border border-border px-4 py-2 text-sm hover:bg-muted transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!formData.name.trim() || isSaving}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2 transition-colors"
          >
            {isSaving ? (
              <>
                <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
